import { RUNWARE_CONFIG, RunwareRequest, RunwareResponse } from '@/config/runware'
import { supabase } from '@/config/supabase'

// Polyfill for crypto.randomUUID for older browsers
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  
  // Fallback implementation for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

class RunwareService {
  constructor() {
    // API key artık edge function'da yönetiliyor
  }

  async generateImage(prompt: string, options?: Partial<RunwareRequest>): Promise<RunwareResponse> {
    try {
      // Runware.ai requires array format with authentication first, then task
      const taskUUID = generateUUID()
      
      // Edge function'a gönderilecek payload (sadece task kısmı, auth edge function'da yapılıyor)
      const payload = {
        taskType: 'imageInference',
        model: RUNWARE_CONFIG.defaultModel,
        numberResults: 1,
        outputFormat: RUNWARE_CONFIG.defaultSettings.outputFormat,
        width: options?.width ?? RUNWARE_CONFIG.defaultSettings.width,
        height: options?.height ?? RUNWARE_CONFIG.defaultSettings.height,
        steps: options?.steps ?? RUNWARE_CONFIG.defaultSettings.steps,
        CFGScale: options?.CFGScale ?? RUNWARE_CONFIG.defaultSettings.CFGScale,
        scheduler: RUNWARE_CONFIG.defaultSettings.scheduler,
        includeCost: RUNWARE_CONFIG.defaultSettings.includeCost,
        outputType: RUNWARE_CONFIG.defaultSettings.outputType,
        checkNSFW: true,
        positivePrompt: prompt,
        negativePrompt: options?.negativePrompt ?? RUNWARE_CONFIG.defaultSettings.negativePrompt,
        seed: options?.seed,
        taskUUID,
      }

      console.log('Generating image with prompt:', prompt)
      
      // Edge function üzerinden istek yap
      const { data: raw, error } = await supabase.functions.invoke('ai-proxy', {
        body: {
          action: 'runware-generate',
          payload: payload
        }
      })

      if (error) {
        throw new Error(`Runware API error via Edge Function: ${error.message}`)
      }

      if (!raw) {
        throw new Error('Runware API\'den yanıt alınamadı')
      }
      
      if (raw.error || raw.errors) {
        throw new Error(raw.error || JSON.stringify(raw.errors))
      }

      // Unwrap if API wrapped array under a `data` property
      const envelope = (raw && Array.isArray(raw.data)) ? raw.data : raw
      
      // Runware.ai returns an array; find the imageInference result
      const resultsArray = Array.isArray(envelope) ? envelope : [envelope]
      const result = resultsArray.find((item: any) => item.taskType === 'imageInference')
        || resultsArray.find((item: any) => item.result || item.images || item.url)
        || resultsArray[resultsArray.length - 1]

      // Robust URL extraction across possible shapes
      const tryExtractUrl = (obj: any): string | undefined => {
        if (!obj || typeof obj !== 'object') return undefined
        const direct = obj.imageURL || obj.image_url || obj.url || obj.result?.url || obj.result?.imageURL
        if (typeof direct === 'string' && direct.startsWith('http')) return direct
        if (Array.isArray(obj.images) && obj.images.length > 0) {
          const imgCandidate = obj.images[0].imageURL || obj.images[0].url
          if (typeof imgCandidate === 'string' && imgCandidate.startsWith('http')) return imgCandidate
        }
        // Look deeper in common containers
        const containers = [obj.result, obj.output, obj.outputs, obj.data]
        for (const c of containers) {
          if (!c) continue
          if (Array.isArray(c)) {
            for (const item of c) {
              const found = tryExtractUrl(item)
              if (found) return found
            }
          } else {
            const found = tryExtractUrl(c)
            if (found) return found
          }
        }
        // Fallback: shallow scan for any field that looks like a URL
        for (const key of Object.keys(obj)) {
          const val = (obj as any)[key]
          if (typeof val === 'string' && val.startsWith('http')) return val
          if (val && typeof val === 'object') {
            const found = tryExtractUrl(val)
            if (found) return found
          }
        }
        return undefined
      }

      const imageURL = tryExtractUrl(result)

      if (!imageURL) {
        console.error('No image URL found in response:', raw)
        throw new Error('Görsel URL\'si alınamadı')
      }

      // If URL is present, return immediately
      if (imageURL) {
        return {
          taskUUID: result?.taskUUID || taskUUID,
          imageURL,
          NSFWContent: result?.NSFWContent || result?.nsfw || false,
          timeTaken: result?.timeTaken || result?.duration || 0,
        }
      }

      // Otherwise, poll task status if we have a taskUUID
      const uuid = result?.taskUUID || taskUUID
      if (!uuid) {
        throw new Error('Görsel URL\'si alınamadı')
      }

      const sleep = (ms: number) => new Promise(res => setTimeout(res, ms))
      const maxAttempts = 20
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await sleep(1500)
        const status = await this.getTaskStatus(uuid)
        if (status.imageURL && status.imageURL.startsWith('http')) {
          return status
        }
      }

      throw new Error('Görsel URL\'si alınamadı')
    } catch (error) {
      console.error('Error generating image:', error)
      throw error
    }
  }

  async getTaskStatus(taskUUID: string): Promise<RunwareResponse> {
    try {
      // Edge function üzerinden istek yap
      const { data, error } = await supabase.functions.invoke('ai-proxy', {
        body: {
          action: 'runware-status',
          payload: { taskUUID }
        }
      })

      if (error) {
        throw new Error(`Runware API error via Edge Function: ${error.message}`)
      }

      if (!data) {
        throw new Error('Runware API\'den yanıt alınamadı')
      }
      
      return {
        taskUUID: data.taskUUID || taskUUID,
        imageURL: data.imageURL || data.image_url || '',
        NSFWContent: data.NSFWContent || false,
        timeTaken: data.timeTaken || 0,
      }
    } catch (error) {
      console.error('Error getting task status:', error)
      throw error
    }
  }

  enhancePrompt(basePrompt: string, _word: string): string {
    // Enhance the user's prompt with better descriptive terms
    const enhancedPrompt = `${basePrompt}, highly detailed, digital art, concept art, trending on artstation, 8k resolution, masterpiece, professional lighting`
    // Do NOT auto-prepend round word; keep user input intact
    return enhancedPrompt
  }

  validatePrompt(prompt: string): { isValid: boolean; error?: string } {
    if (!prompt || prompt.trim().length < 5) {
      return {
        isValid: false,
        error: 'Prompt çok kısa. En az 5 karakter olmalı.',
      }
    }

    if (prompt.length > 500) {
      return {
        isValid: false,
        error: 'Prompt çok uzun. Maksimum 500 karakter olmalı.',
      }
    }

    // Check for inappropriate content (basic filter)
    const inappropriateWords = ['nude', 'naked', 'sex', 'violence', 'gore', 'blood']
    const lowerPrompt = prompt.toLowerCase()
    
    for (const word of inappropriateWords) {
      if (lowerPrompt.includes(word)) {
        return {
          isValid: false,
          error: 'Prompt uygunsuz içerik barındırıyor.',
        }
      }
    }

    return { isValid: true }
  }
}

export const runwareService = new RunwareService()
export default runwareService



